import { CHUNK_RELOAD_KEY } from "@/shared/constants";
import { lazy } from "react";

export const lazyRetry = <T>(componentImport: () => Promise<T>): Promise<T> =>
  componentImport().catch((error: unknown) => {
    const message = error instanceof Error ? error.message : String(error);

    const isChunkError =
      message.includes("Failed to fetch dynamically imported module") ||
      message.includes("error loading dynamically imported module") ||
      message.includes("ChunkLoadError");

    if (isChunkError) {
      try {
        const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
        if (!hasReloaded) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");
          window.location.reload();
          return new Promise<T>(() => undefined);
        }
      } catch {
        // sessionStorage unavailable — fall through to reject
      }
    }

    return Promise.reject(error);
  });

// -------- Feature Imports --------

export const importDashboard = (): Promise<
  typeof import("@/features/Dashboard/Dashboard")
> => import("@/features/Dashboard/Dashboard");

export const importLowBatteryMeters = (): Promise<
  typeof import("@/features/Dashboard/LowBatteryMeters")
> => import("@/features/Dashboard/LowBatteryMeters");

export const importOfflineMeters = (): Promise<
  typeof import("@/features/Dashboard/OfflineMeters")
> => import("@/features/Dashboard/OfflineMeters");

export const importMetrixInsights = (): Promise<
  typeof import("@/features/MetrixInsights/MetrixInsights")
> => import("@/features/MetrixInsights/MetrixInsights");

export const importSupersetConfiguration = (): Promise<
  typeof import("@/features/SupersetConfiguration/SupersetConfiguration")
> => import("@/features/SupersetConfiguration/SupersetConfiguration");

export const importProfile = (): Promise<
  typeof import("@/features/Profile/Profile")
> => import("@/features/Profile/Profile");

export const importAuthentication = (): Promise<
  typeof import("@/features/Authentication/Authentication")
> => import("@/features/Authentication/Authentication");

export const importMeters = (): Promise<
  typeof import("@/features/Meters/Meters")
> => import("@/features/Meters/Meters");

export const importMeterGroup = (): Promise<
  typeof import("@/features/MeterGroup/MeterGroup")
> => import("@/features/MeterGroup/MeterGroup");

export const importSite = (): Promise<typeof import("@/features/Site/Site")> =>
  import("@/features/Site/Site");

export const importUser = (): Promise<typeof import("@/features/User/User")> =>
  import("@/features/User/User");

export const importRole = (): Promise<typeof import("@/features/Role/Role")> =>
  import("@/features/Role/Role");

export const importContact = (): Promise<
  typeof import("@/features/Contact/Contact")
> => import("@/features/Contact/Contact");

export const importCompanySettings = (): Promise<
  typeof import("@/features/CompanySettings/CompanySettings")
> => import("@/features/CompanySettings/CompanySettings");

export const importVirtualMeter = (): Promise<
  typeof import("@/features/VirtualMeter/VirtualMeter")
> => import("@/features/VirtualMeter/VirtualMeter");

export const importReportENem12 = (): Promise<
  typeof import("@/features/eNEM12Report/eNEM12")
> => import("@/features/eNEM12Report/eNEM12");

export const importReportNem12 = (): Promise<
  typeof import("@/features/NEM12Report/NEM12")
> => import("@/features/NEM12Report/NEM12");

export const importReportMdn = (): Promise<
  typeof import("@/features/MDNReport/MDN")
> => import("@/features/MDNReport/MDN");

export const importReportNem12QualityFlag = (): Promise<
  typeof import("@/features/NEM12QualityFlagReport/NEM12QualityFlag")
> => import("@/features/NEM12QualityFlagReport/NEM12QualityFlag");

export const importReportMeterEvent = (): Promise<
  typeof import("@/features/MeterEventReport/MeterEvent")
> => import("@/features/MeterEventReport/MeterEvent");

export const importReportOrionCumulative = (): Promise<
  typeof import("@/features/OrionCumulativeReport/OrionCumulative")
> => import("@/features/OrionCumulativeReport/OrionCumulative");

export const importReportSiteUsage = (): Promise<
  typeof import("@/features/SiteUsageReport/SiteUsage")
> => import("@/features/SiteUsageReport/SiteUsage");

export const importDataValidationReport = (): Promise<
  typeof import("@/features/DataValidationReport/DataValidation")
> => import("@/features/DataValidationReport/DataValidation");

export const reportHistory = (): Promise<
  typeof import("@/features/ReportHistory/ReportHistory")
> => import("@/features/ReportHistory/ReportHistory");

export const importValidationSummary = (): Promise<
  typeof import("@/features/ValidationSummary/ValidationSummary")
> => import("@/features/ValidationSummary/ValidationSummary");

export const importAdvancedEcoValidation = (): Promise<
  typeof import("@/features/AdvancedEcoValidation/AdvancedEcoValidation")
> => import("@/features/AdvancedEcoValidation/AdvancedEcoValidation");

export const validationHistory = (): Promise<
  typeof import("@/features/ValidationHistory/ValidationHistory")
> => import("@/features/ValidationHistory/ValidationHistory");

export const importDeviceStatus = (): Promise<
  typeof import("@/features/DeviceStatus/DeviceStatus")
> => import("@/features/DeviceStatus/DeviceStatus");

export const importDataCorrections = (): Promise<
  typeof import("@/features/DataCorrections/DataCorrections")
> => import("@/features/DataCorrections/DataCorrections");

export const importSerialGenerator = (): Promise<
  typeof import("@/features/SerialGenerator/SerialGenerator")
> => import("@/features/SerialGenerator/SerialGenerator");

export const importPropertyGenerator = (): Promise<
  typeof import("@/features/PropertyGenerator/PropertyGenerator")
> => import("@/features/PropertyGenerator/PropertyGenerator");

export const importNotFound = (): Promise<
  typeof import("@/features/NotFound/NotFound")
> => import("@/features/NotFound/NotFound");

export const importNMIGenerator = (): Promise<
  typeof import("@/features/NMIGenerator/NMIGenerator")
> => import("@/features/NMIGenerator/NMIGenerator");

export const importIotSync = (): Promise<
  typeof import("@/features/IotSync/IotSync")
> => import("@/features/IotSync/IotSync");

export const importCetSync = (): Promise<
  typeof import("@/features/CetSync/CetSync")
> => import("@/features/CetSync/CetSync");

export const importAdvancedEcoSync = (): Promise<
  typeof import("@/features/AdvancedEcoSync/AdvancedEcoSync")
> => import("@/features/AdvancedEcoSync/AdvancedEcoSync");

export const importImportMeters = (): Promise<
  typeof import("@/features/ImportMeters/ImportMeters")
> => import("@/features/ImportMeters/ImportMeters");

export const importUnprovisionedMeters = (): Promise<
  typeof import("@/features/UnprovisionedMeters/UnprovisionedMeters")
> => import("@/features/UnprovisionedMeters/UnprovisionedMeters");

export const importUnprovisionedIotDevices = (): Promise<
  typeof import("@/features/UnprovisionedIotDevices/UnprovisionedIotDevices")
> => import("@/features/UnprovisionedIotDevices/UnprovisionedIotDevices");

export const platformConfiguration = (): Promise<
  typeof import("@/features/PlatformConfiguration/PlatformConfiguration")
> => import("@/features/PlatformConfiguration/PlatformConfiguration");

export const userAuditLogs = (): Promise<
  typeof import("@/features/UserAuditLogs/UserAuditLogs")
> => import("@/features/UserAuditLogs/UserAuditLogs");

export const logMaintenance = (): Promise<
  typeof import("@/features/LogMaintenance/LogMaintenance")
> => import("@/features/LogMaintenance/LogMaintenance");

export const systemLogs = (): Promise<
  typeof import("@/features/SystemLogs/SystemLogs")
> => import("@/features/SystemLogs/SystemLogs");

export const meterStatusCodes = (): Promise<
  typeof import("@/features/MeterStatusCodes/MeterStatusCodes")
> => import("@/features/MeterStatusCodes/MeterStatusCodes");

export const alarmEvents = (): Promise<
  typeof import("@/features/AlarmEvents/AlarmEvents")
> => import("@/features/AlarmEvents/AlarmEvents");

export const importMeterSchedules = (): Promise<
  typeof import("@/features/MeterSchedules/MeterSchedules")
> => import("@/features/MeterSchedules/MeterSchedules");

export const importIotDataSummary = (): Promise<
  typeof import("@/features/IotDataSummary/IotDataSummary")
> => import("@/features/IotDataSummary/IotDataSummary");

export const importAnomalyDashboard = (): Promise<
  typeof import("@/features/AnomalyDashboard/AnomalyDashboard")
> => import("@/features/AnomalyDashboard/AnomalyDashboard");

export const meterDataAudit = (): Promise<
  typeof import("@/features/MeterDataAudit/MeterDataAudit")
> => import("@/features/MeterDataAudit/MeterDataAudit");

export const importAlarmProperty = (): Promise<
  typeof import("@/features/AlarmProperty/AlarmProperty")
> => import("@/features/AlarmProperty/AlarmProperty");

export const contactSupport = (): Promise<
  typeof import("@/features/ContactSupport/ContactSupport")
> => import("@/features/ContactSupport/ContactSupport");

export const headerContactSupport = (): Promise<
  typeof import("@/features/ContactSupport/HeaderContactSupport")
> => import("@/features/ContactSupport/HeaderContactSupport");

export const importNotificationLog = (): Promise<
  typeof import("@/features/NotificationLog/NotificationLog")
> => import("@/features/NotificationLog/NotificationLog");

export const importNEM12Viewer = (): Promise<
  typeof import("@/features/NEM12/NEM12Viewer")
> => import("@/features/NEM12/NEM12Viewer");

// --------------------
// Lazy components (declared as const)
// --------------------
export const Dashboard = lazy(() => lazyRetry(importDashboard));

export const LowBatteryMeters = lazy(() => lazyRetry(importLowBatteryMeters));

export const OfflineMeters = lazy(() => lazyRetry(importOfflineMeters));

export const Meters = lazy(() => lazyRetry(importMeters));

export const MeterGroup = lazy(() => lazyRetry(importMeterGroup));

export const Site = lazy(() => lazyRetry(importSite));

export const User = lazy(() => lazyRetry(importUser));

export const Role = lazy(() => lazyRetry(importRole));

export const Contact = lazy(() => lazyRetry(importContact));

export const CompanySettings = lazy(() => lazyRetry(importCompanySettings));

export const VirtualMeter = lazy(() => lazyRetry(importVirtualMeter));

export const ReportENem12 = lazy(() => lazyRetry(importReportENem12));

export const ReportNem12 = lazy(() => lazyRetry(importReportNem12));

export const ReportMdn = lazy(() => lazyRetry(importReportMdn));

export const ReportNem12QualityFlag = lazy(() =>
  lazyRetry(importReportNem12QualityFlag),
);

export const ReportMeterEvent = lazy(() => lazyRetry(importReportMeterEvent));

export const ReportOrionCumulative = lazy(() =>
  lazyRetry(importReportOrionCumulative),
);

export const ReportSiteUsage = lazy(() => lazyRetry(importReportSiteUsage));

export const DataValidationReport = lazy(() =>
  lazyRetry(importDataValidationReport),
);

export const ReportHistory = lazy(() => lazyRetry(reportHistory));

export const ValidationHistory = lazy(() => lazyRetry(validationHistory));

export const ValidationSummary = lazy(() => lazyRetry(importValidationSummary));

export const AdvancedEcoValidation = lazy(() =>
  lazyRetry(importAdvancedEcoValidation),
);

export const DeviceStatus = lazy(() => lazyRetry(importDeviceStatus));

export const DataCorrections = lazy(() => lazyRetry(importDataCorrections));

export const SerialGenerator = lazy(() => lazyRetry(importSerialGenerator));

export const PropertyGenerator = lazy(() => lazyRetry(importPropertyGenerator));

export const NotFound = lazy(() => lazyRetry(importNotFound));

export const NMIGenerator = lazy(() => lazyRetry(importNMIGenerator));

export const IotSync = lazy(() => lazyRetry(importIotSync));

export const CetSync = lazy(() => lazyRetry(importCetSync));

export const AdvancedEcoSync = lazy(() => lazyRetry(importAdvancedEcoSync));

export const ImportMeters = lazy(() => lazyRetry(importImportMeters));

export const UnprovisionedMeters = lazy(() =>
  lazyRetry(importUnprovisionedMeters),
);

export const UnprovisionedIotDevices = lazy(() =>
  lazyRetry(importUnprovisionedIotDevices),
);

export const PlatformConfiguration = lazy(() =>
  lazyRetry(platformConfiguration),
);

export const UserAuditLogs = lazy(() => lazyRetry(userAuditLogs));

export const LogMaintenance = lazy(() => lazyRetry(logMaintenance));

export const SystemLogs = lazy(() => lazyRetry(systemLogs));

export const MeterStatusCodes = lazy(() => lazyRetry(meterStatusCodes));

export const AlarmEvents = lazy(() => lazyRetry(alarmEvents));

export const IotDataSummary = lazy(() => lazyRetry(importIotDataSummary));

export const AnomalyDashboard = lazy(() => lazyRetry(importAnomalyDashboard));

export const MetrixInsights = lazy(() => lazyRetry(importMetrixInsights));

export const SupersetConfiguration = lazy(() =>
  lazyRetry(importSupersetConfiguration),
);

export const Profile = lazy(() => lazyRetry(importProfile));

export const Authentication = lazy(() => lazyRetry(importAuthentication));

export const MeterDataAudit = lazy(() => lazyRetry(meterDataAudit));

export const AlarmProperty = lazy(() => lazyRetry(importAlarmProperty));

export const MeterSchedules = lazy(() => lazyRetry(importMeterSchedules));

export const ContactSupport = lazy(() => lazyRetry(contactSupport));

export const HeaderContactSupport = lazy(() => lazyRetry(headerContactSupport));

export const NotificationLog = lazy(() => lazyRetry(importNotificationLog));

export const NEM12Viewer = lazy(() => lazyRetry(importNEM12Viewer));
